import { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import SessionExpiredModal from "./components/SessionExpiredModal";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import lazyWithRetry from "./utils/lazyWithRetry";
import { Toaster } from "sonner";
import "./App.css";

// ─── Lazy-loaded route components ──────────────────────────────────
// Heavy pages are code-split so the initial JS bundle only ships
// Dashboard + Login + shell. Every other page is fetched on demand
// the first time the operator navigates to it. Cuts initial parse
// time significantly on cold loads (especially on slow warehouse
// tablets).
//
// `lazyWithRetry` retries the dynamic import a few times and — if the
// chunk really can't be fetched (usually because a fresh deploy
// invalidated the old chunk hash) — does a single full-page reload.
// Prevents the "URL changes but page never mounts" symptom that
// otherwise shows up right after every deploy or HMR restart.
const Masters = lazyWithRetry(() => import("./pages/Masters"));
const FabricReceipts = lazyWithRetry(() => import("./pages/FabricReceipts"));
const Rolls = lazyWithRetry(() => import("./pages/Rolls"));
const RollDetail = lazyWithRetry(() => import("./pages/RollDetail"));
const RollLabel = lazyWithRetry(() => import("./pages/RollLabel"));
const Pipeline = lazyWithRetry(() => import("./pages/Pipeline"));
const Cutting = lazyWithRetry(() => import("./pages/Cutting"));
const CuttingOrders = lazyWithRetry(() => import("./pages/CuttingOrders"));
const CuttingOrderPrint = lazyWithRetry(() => import("./pages/CuttingOrderPrint"));
const Batches = lazyWithRetry(() => import("./pages/Batches"));
const BatchDetail = lazyWithRetry(() => import("./pages/BatchDetail"));
const Designs = lazyWithRetry(() => import("./pages/Designs"));
const Printing = lazyWithRetry(() => import("./pages/Printing"));
const Sewing = lazyWithRetry(() => import("./pages/Sewing"));
const QC = lazyWithRetry(() => import("./pages/QC"));
const Ironing = lazyWithRetry(() => import("./pages/Ironing"));
const Stock = lazyWithRetry(() => import("./pages/Stock"));
const FinishedStock = lazyWithRetry(() => import("./pages/FinishedStock"));
const Dispatch = lazyWithRetry(() => import("./pages/Dispatch"));
const JobWork = lazyWithRetry(() => import("./pages/JobWork"));
const Reports = lazyWithRetry(() => import("./pages/Reports"));
const ChallanPrint = lazyWithRetry(() => import("./pages/ChallanPrint"));
const FabricReceiptPrint = lazyWithRetry(() => import("./pages/FabricReceiptPrint"));
const Returns = lazyWithRetry(() => import("./pages/Returns"));
const ReturnChallanPrint = lazyWithRetry(() => import("./pages/ReturnChallanPrint"));
const GRNPrint = lazyWithRetry(() => import("./pages/GRNPrint"));
const Scan = lazyWithRetry(() => import("./pages/Scan"));
const ChangePassword = lazyWithRetry(() => import("./pages/ChangePassword"));
const VendorPerformance = lazyWithRetry(() => import("./pages/VendorPerformance"));
const ActivityLog = lazyWithRetry(() => import("./pages/ActivityLog"));
const Trash = lazyWithRetry(() => import("./pages/Trash"));
const Unicommerce = lazyWithRetry(() => import("./pages/Unicommerce"));

function RouteFallback() {
  return (
    <div
      data-testid="route-suspense-fallback"
      className="w-full py-16 flex items-center justify-center text-slate-500 text-sm"
    >
      <div className="animate-pulse">Loading…</div>
    </div>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <SessionExpiredModal />
          <RouteErrorBoundary>
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/rolls/:id/label" element={<ProtectedRoute><RollLabel /></ProtectedRoute>} />
              <Route path="/receipts/:id/print" element={<ProtectedRoute><FabricReceiptPrint /></ProtectedRoute>} />
              <Route path="/challan/:kind/:id" element={<ProtectedRoute><ChallanPrint /></ProtectedRoute>} />
              <Route path="/grn/:kind/:id" element={<ProtectedRoute><GRNPrint /></ProtectedRoute>} />
              <Route path="/cutting-order/:id" element={<ProtectedRoute><CuttingOrderPrint /></ProtectedRoute>} />
              <Route path="/fabric-return/:id" element={<ProtectedRoute><ReturnChallanPrint /></ProtectedRoute>} />
              <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/masters" element={<Masters />} />
                <Route path="/receipts" element={<FabricReceipts />} />
                <Route path="/rolls" element={<Rolls />} />
                <Route path="/returns" element={<Returns />} />
                <Route path="/rolls/:id" element={<RollDetail />} />
                <Route path="/pipeline" element={<Pipeline />} />
                <Route path="/cutting" element={<Cutting />} />
                <Route path="/cutting-orders" element={<CuttingOrders />} />
                <Route path="/batches" element={<Batches />} />
                <Route path="/batches/:id" element={<BatchDetail />} />
                <Route path="/designs" element={<Designs />} />
                <Route path="/printing" element={<Printing />} />
                <Route path="/sewing" element={<Sewing />} />
                <Route path="/qc" element={<QC />} />
                <Route path="/ironing" element={<Ironing />} />
                <Route path="/stock" element={<Stock />} />
                <Route path="/finished-stock" element={<FinishedStock />} />
                <Route path="/dispatch" element={<Dispatch />} />
                <Route path="/dispatch/:id" element={<Dispatch />} />
                <Route path="/job-work" element={<JobWork />} />
                <Route path="/job-work/:id" element={<JobWork />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/vendor-performance" element={<VendorPerformance />} />
                <Route path="/activity" element={<ActivityLog />} />
                <Route path="/trash" element={<Trash />} />
                <Route path="/unicommerce" element={<Unicommerce />} />
                <Route path="/scan" element={<Scan />} />
                <Route path="/account/password" element={<ChangePassword />} />
              </Route>
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Suspense>
          </RouteErrorBoundary>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
